import React, { ReactElement, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import Headline from '../../common/Headline';
import { getPatternModal } from '../../../style';
import ButtonSet from '../../blocks/ButtonSet';
import Button from '../../../atoms/buttons/Button';
import { IsMobileViewPort } from '../../../utils/helpers';
import Checkbox from '../../../atoms/Checkbox';
import borders from '../../../theme/borders';
import colors from '../../../theme/colors';
import breakpoints from 'theme/material/breakpoints';
import { ButtonType } from 'atoms/buttons/types/Button';
import { isMobile } from 'react-device-detect';
import { DashlineSeparator } from 'components/join/styledElements';

const ContainerDiv = styled('div')({
  paddingTop: 0,
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
    [breakpoints.down(375.05)]: {
      paddingTop: 12,
    },
  })
);

const ButtonDiv = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: isMobile ? 10 : 20,
  [breakpoints.down(374.05)]: {
    flexWrap: 'wrap',
  },
});

export interface ConfirmationProps {
  title: string;
  message: string | React.ReactNode;
  buttonID?: string;
  cancelText?: string;
  confirmText?: string;
  includeDoNotShowAgainOption?: boolean;
  includeDoNotShowAgainLabel?: string;
  doNotShowAgainValue?: boolean;
  cancelAction?: () => void;
  confirmAction: (doNotShowAgain?: boolean) => void;
  confirmBtnType?: ButtonType;
}

const ConfirmationV2 = ({
  title = '',
  message = '',
  buttonID = '',
  cancelText = 'Nevermind',
  confirmText,
  cancelAction,
  confirmAction,
  includeDoNotShowAgainOption,
  includeDoNotShowAgainLabel,
  doNotShowAgainValue,
}: ConfirmationProps): ReactElement => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(doNotShowAgainValue);

  return (
    <ContainerDiv>
      <HeaderDiv>
        <TitleDiv>{title}</TitleDiv>
        <Headline
          style={{
            color: getPatternModal().headerColor,
            fontSize: 16,
            lineHeight: '21px',
            letterSpacing: -0.3,
            width: isMobile ? '215px' : '99%',
            justifySelf: 'center',
            marginBottom: 40,
          }}
          data-test-id="profile-offer-headline"
          size="SM"
          dangerouslySetInnerHTML={{
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            __html: message,
          }}
        />
      </HeaderDiv>
      <ButtonDiv>
        <ButtonSet align="center">
          <Button
            id={buttonID}
            buttonType="secondaryBlue"
            onClick={cancelAction}
            type="submit"
            customStyle={{
              fontSize: IsMobileViewPort ? 14 : 18,
              minWidth: 134,
              padding: isMobile ? '12px 22px' : '16px 24px',
            }}
            buttonTextCustomStyle={{
              fontWeight: isMobile ? 600 : 700,
              lineHeight: '16px',
              letterSpacing: isMobile ? -0.28 : -0.36,
            }}
            data-test-id="modal-decline-offer-btn"
          >
            {cancelText}
          </Button>
        </ButtonSet>
        <ButtonSet align="center">
          <Button
            buttonType="primaryRed"
            data-test-id={`${buttonID}Cancel`}
            customStyle={{
              fontSize: IsMobileViewPort ? 14 : 18,
              minWidth: 134,
              padding: isMobile ? '12px 22px' : '16px 24px',
            }}
            buttonTextCustomStyle={{
              fontWeight: isMobile ? 600 : 700,
              lineHeight: '16px',
              letterSpacing: isMobile ? -0.28 : -0.36,
            }}
            onClick={e => {
              confirmAction(doNotShowAgain);
              e.preventDefault();
            }}
          >
            {confirmText}
          </Button>
        </ButtonSet>
      </ButtonDiv>
      {includeDoNotShowAgainOption && (
        <>
          <DashlineSeparator style={{ margin: '20px 0' }} />
          <Checkbox
            label={includeDoNotShowAgainLabel || 'Do not show this again'}
            onChange={() => {
              setDoNotShowAgain(!doNotShowAgain);
            }}
            value={doNotShowAgain}
            labelCustomStyle={{ textAlign: 'center', margin: 0 }}
            customLabelTextStyle={{
              color: borders.dark.line1,
              paddingLeft: 4,
              fontSize: 13,
              lineHeight: '16px',
              letterSpacing: -0.3,
              paddingTop: 1,
              position: 'relative',
              top: 0.5,
            }}
            customStyle={{
              height: 12,
              width: 12,
              backgroundColor: doNotShowAgain ? colors.light.blue.tertiary : 'inherit',
              borderColor: colors.light.blue.tertiary,
            }}
            svgCustomStyle={{ height: 25, width: 26, paddingTop: 1, paddingLeft: 0.3 }}
          />
        </>
      )}
    </ContainerDiv>
  );
};

export default ConfirmationV2;
